/**
 * Top-level screen routing. Intentionally not a real router — there are
 * no URLs, no history, no deep-linking. Just a single `screen` value the
 * app branches on, plus a `navigate` function to change it.
 *
 * The screen value is a discriminated union so adding new destinations
 * (Phase 3+ gameplay screens) is a non-breaking addition.
 *
 * Surface as of Phase 7 slice 6:
 *   home                       — the three-button home for Eva
 *   parent                     — gated parent area shell (word editor / dashboard / settings)
 *   flashCard                  — Phase 3 study window
 *   phaseIntro:firstPass       — Pumpkin-led "what's next" beat before first pass
 *   firstPass                  — Phase 4 spelling pass (one word at a time)
 *   results                    — Phase 4 visual diff of all 10 first-pass attempts
 *   phaseIntro:secondChance    — Pumpkin-led "what's next" beat before second chance
 *   secondChance               — Phase 5 retry for first-pass-missed words
 *   phaseIntro:threeXWrite     — Pumpkin-led "what's next" beat before 3x-write
 *   threeXWrite                — Phase 5 paper-write tracker for first-pass-missed words
 *   celebration                — Phase 6 end-of-session celebration + persist + stickers
 *   myStickers                 — Phase 6 sticker collection
 *   alphabet                   — Phase 9 alphabet collection board
 *   pastWeeks                  — Phase 7 archived-week picker (review flow)
 */
/* eslint-disable react-refresh/only-export-components --
 * Provider + hook + types co-located by design. */
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

export type AppScreen =
  | "home"
  | "parent"
  | "flashCard"
  | "phaseIntro:firstPass"
  | "firstPass"
  | "results"
  | "phaseIntro:secondChance"
  | "secondChance"
  | "phaseIntro:threeXWrite"
  | "threeXWrite"
  | "celebration"
  | "myStickers"
  | "alphabet"
  | "pastWeeks";

export interface NavigationContextValue {
  screen: AppScreen;
  navigate: (next: AppScreen) => void;
}

const NavigationContext = createContext<NavigationContextValue | null>(null);

export function NavigationProvider({ children }: { children: ReactNode }) {
  const [screen, setScreen] = useState<AppScreen>("home");
  const navigate = useCallback((next: AppScreen) => setScreen(next), []);
  const value = useMemo<NavigationContextValue>(() => ({ screen, navigate }), [screen, navigate]);
  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigation(): NavigationContextValue {
  const ctx = useContext(NavigationContext);
  if (!ctx) throw new Error("useNavigation must be called inside <NavigationProvider>");
  return ctx;
}
